import { useGetArtworkById } from "@/api/artwork/artwork";
import { useGetAttachmentsByDocId } from "@/api/attachment";
import { getCollectors } from "@/api/collector/collector";
import {
  Tirage,
  useGetTirage,
  useUpdateTirage,
} from "@/api/tirages/tirage.api";
import {
  appendNoDuplicateRecordNumberRefine,
  tirageSchemaUpdate,
} from "@/api/tirages/tirage.validation";
import { useAddToTrash } from "@/api/trash";
import * as AF from "@/components/form-items/AbstractForm";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import { MultilingualField } from "@/components/form-items/MultilingualField";
import { AbstractFormSelectFetcher } from "@/components/form-items/select-fetcher-form";
import MediaLibrary, { ImageFormData } from "@/components/images/MediaLibrary";
import StyledDrawer from "@/components/StyledDrawer";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { SeparatorWithText } from "@/components/ui/separator";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { SupportedLocale } from "@/i18n";
import { TypeOptionPosition, getDefaultImage } from "@/lib/image";
import type { Collector } from "@/lib/types";
import { cn, generateImageName } from "@/lib/utils";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { Cross1Icon } from "@radix-ui/react-icons";
import * as Tabs from "@radix-ui/react-tabs";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ChevronDownIcon, ImageIcon, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MinifiedTwoDimensionalInput } from "../form-items/DimensionsInput";
import LinksManagement from "../LinksManagement";
import { UniqueImageFullScreen } from "../modal/UniqueImageFullScreen";
import PrintButton from "../print/PrintButton";
import { BadgeCell } from "../table/CustomCell";
import { Badge } from "../ui/badge";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "../ui/menubar";
import { TirageHistoryTable } from "./TirageHistoryTable";

const updateSchema = appendNoDuplicateRecordNumberRefine(
  tirageSchemaUpdate.passthrough(),
);

type TirageSheetFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tirageId?: string;
  artworkId: string;
  initialData?: Partial<Tirage>;
};

const TirageSheetForm = ({
  open,
  onOpenChange,
  tirageId,
  artworkId: _artworkId,
  initialData,
}: TirageSheetFormProps) => {
  const { t } = useTranslation();
  const { data: tirageData, isLoading } = useGetTirage(tirageId);
  const { data: artwork } = useGetArtworkById(_artworkId);
  const { mutateAsync: updateTirage } = useUpdateTirage();
  const trashAdder = useAddToTrash();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isReader = user?.role === "reader" ? true : false;
  const [isMediaDrawerOpen, setIsMediaDrawerOpen] = useState(false);
  const { data: tirageAttachments } = useGetAttachmentsByDocId(tirageId);

  const defaultTirageImage = useMemo(
    () =>
      tirageAttachments
        ? getDefaultImage(tirageAttachments, "tirage")
        : undefined,
    [tirageAttachments],
  );

  const handleInvalidateAttachments = () => {
    if (!tirageId) return;
    queryClient.invalidateQueries({
      queryKey: ["attachment", { docId: tirageId }],
    });
    // Invalidate all tirage queries for this artwork to refresh the table
    queryClient.invalidateQueries({
      queryKey: ["tirages", "artwork"],
      exact: false,
    });
    // Also invalidate the specific tirage query
    queryClient.invalidateQueries({
      queryKey: ["tirage", tirageId],
    });
  };

  const computeTirageImageName = (data?: ImageFormData) => {
    if (!artwork) return "";

    return generateImageName({
      primaryType: "artwork",
      artwork,
      translatedPosition: data?.position ? t(data.position) : undefined,
    });
  };

  const defaultValues = useMemo(() => {
    return {
      ...initialData,
      ...tirageData,
    };
  }, [initialData, tirageData]);

  const form = useForm<FieldValues>({
    reValidateMode: "onSubmit",
    defaultValues,
    resolver: standardSchemaResolver(updateSchema),
  });

  const hasInitialized = useRef(false);
  const lastTirageId = useRef<string | undefined>(tirageId);
  const lastOpenState = useRef<boolean>(open);
  const shouldInitializeRef = useRef<boolean>(false);

  useEffect(() => {
    const tirageIdChanged = lastTirageId.current !== tirageId;
    const sheetJustOpened = open && !lastOpenState.current;

    // Reset initialization flag when tirageId changes or sheet opens
    if (tirageIdChanged || sheetJustOpened) {
      hasInitialized.current = false;
      shouldInitializeRef.current = true;
      lastTirageId.current = tirageId;
    }
    lastOpenState.current = open;

    // Only initialize or reset form when:
    // 1. We need to initialize (tirageId changed or sheet just opened)
    // 2. Form hasn't been initialized yet
    // 3. We have default values available
    // This prevents resetting the form when data is refetched due to tab visibility changes
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      if (!hasInitialized.current && shouldInitializeRef.current) {
        form.reset(defaultValues);
        hasInitialized.current = true;
        shouldInitializeRef.current = false;
      }
    }
  }, [defaultValues, form, tirageId, open]);

  const handleAddToTrash = () => {
    if (tirageData?.tiragePublicationStatus === "bin") {
      toast({
        title: t("documentAlreadyInTrashToastTitle"),
        variant: "success",
        description: t("documentAlreadyInTrashToastDescription"),
      });
    } else {
      trashAdder.mutate(
        { documentId: tirageId, documentType: "tirage" },
        {
          onSuccess: () => {
            toast({
              title: t("documentAddedToTrashToastTitle"),
              variant: "success",
              description: t("documentAddedToTrashToastDescription"),
            });
            form.reset(undefined, { keepValues: true });
          },
        },
      );
    }
  };

  const onSubmit = async (
    data: FieldValues,
    publishState: "draft" | "published" = "draft",
  ) => {
    if (!tirageId) {
      // This shouldn't happen in edit mode, but handle it gracefully
      return;
    }

    try {
      // Format data for API submission
      const submitData: Record<string, any> = {
        ...data,
        tiragePublicationStatus: publishState,
      };

      // Remove undefined values
      Object.keys(submitData).forEach((key) => {
        if (submitData[key] === undefined) {
          delete submitData[key];
        }
      });

      await updateTirage({
        id: tirageId,
        updateTirageDto: submitData,
        artworkId: _artworkId,
      });

      toast({
        title: t("recordSuccessfullyUpdated"),
        duration: 3000,
        variant: "success",
      });
      //onOpenChange(false);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("tirageDescriptionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      toast({
        title: t("errorUpdatingRecord"),
        description: isAxiosError(err) ? err.message : t("unknownError"),
        variant: "error",
      });
    }
  };

  const title = useMemo(() => {
    return artwork?.oeuvresDescriptionTitre
      ? artwork?.oeuvresDescriptionTitre +
          " - " +
          artwork?.oeuvresDescriptionDateDeCreation
      : "";
  }, [artwork]);

  const tabs = [
    {
      value: "generalInformation",
      label: t("tirageGeneralInformation"),
    },
    {
      value: "printHistory",
      label: t("tiragePrintHistory"),
    },
    {
      value: "links",
      label: t("linkedEntries"),
    },
    {
      value: "technicalNotes",
      label: t("tirageTechnicalNotes"),
    },
    {
      value: "conditionNotes",
      label: t("tirageConditionNotes"),
    },
  ];

  // Local state for tabs (isolated from URL)
  const [activeTab, setActiveTab] = useState(
    tabs[0]?.value || "generalInformation",
  );

  if (isLoading && tirageId) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[800px] px-0">
          <div className="flex items-center justify-center h-full">
            <p>{t("loading")}</p>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="sm:max-w-6xl w-full px-0 max-h-screen overflow-y-auto flex flex-col"
        closeIconLeft={false}
        showPrimitiveCross={false}
      >
        <SheetHeader className="p-8 pt-4 pb-0 gap-2 flex-shrink-0 flex flex-row items-start justify-between">
          <div className="flex items-start gap-2 min-w-[70%]">
            <div className="flex items-center gap-2 mt-0.5">
              <SheetClose>
                <Cross1Icon className="h-4 w-4 relative text-gray-400" />
              </SheetClose>
              <BadgeCell
                value={t(tirageData?.tiragePublicationStatus ?? "")}
                variant={
                  tirageData?.tiragePublicationStatus === "published"
                    ? "published"
                    : "draft"
                }
              />
            </div>
            <SheetTitle className="w-full">
              {title} - {t("tirageKeyLabelTitleForm")}{" "}
              {tirageData?.tirageDescriptionNumeroEnregistrement}
            </SheetTitle>
          </div>
          <div className="flex items-start justify-end gap-2 !m-0 w-full">
            <Button
              variant="outline"
              onClick={() => handleAddToTrash()}
              disabled={
                trashAdder.isPending ||
                tirageData?.tiragePublicationStatus === "bin" ||
                isReader
              }
            >
              <Trash2 className="w-5 h-5 text-[#DC2626]" />
            </Button>
            <PrintButton
              hideText={true}
              iconSize={18}
              iconColor="#64748B"
              disabled={true}
            />
            <Menubar className="border-none">
              <MenubarMenu>
                <MenubarTrigger
                  className={cn(
                    "px-3 py-2 rounded-lg justify-center items-center gap-2 flex bg-primary text-white",
                    isReader && "opacity-50 cursor-default",
                  )}
                  disabled={isReader}
                >
                  <p className="text-sm font-semibold font-['Raleway']">
                    {t("saveSheet")}
                  </p>
                  <ChevronDownIcon className="w-5 h-5 " />
                </MenubarTrigger>
                <MenubarContent>
                  <MenubarItem
                    onClick={form.handleSubmit((data) =>
                      onSubmit(data, "draft"),
                    )}
                  >
                    {t("saveAsDraft")}
                  </MenubarItem>
                  <MenubarItem
                    onClick={form.handleSubmit((data) =>
                      onSubmit(data, "published"),
                    )}
                  >
                    {t("saveAndPublish")}
                  </MenubarItem>
                </MenubarContent>
              </MenubarMenu>
            </Menubar>
          </div>
        </SheetHeader>

        <Form {...form}>
          <Tabs.Root
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex flex-col flex-1 min-h-0"
          >
            <Tabs.TabsList className="flex flex-row gap-1 px-8 pb-2 border-b flex-shrink-0 ">
              <div className="bg-[#F1F5F9] rounded-md p-1">
                {tabs.map((tab) => (
                  <Tabs.TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="px-3 py-1 rounded-md text-[12px] font-medium transition-colors data-[state=active]:bg-white data-[state=active]:text-gray-700  data-[state=inactive]:text-gray-700 hover:text-foreground"
                  >
                    {tab.label}
                  </Tabs.TabsTrigger>
                ))}
              </div>
            </Tabs.TabsList>

            <div className="flex-1 overflow-y-auto">
              <Tabs.TabsContent
                value="generalInformation"
                className="px-11 py-5 gap-8"
              >
                <AF.Row className="items-start">
                  <AF.Col>
                    {/* Attachments / Media library management */}
                    {tirageId && (
                      <div className="mb-6 flex items-end justify-end w-full">
                        <StyledDrawer
                          title={t("titreModalGestionImagesTirage")}
                          maxWidth="sm:max-w-3xl"
                          sheetProps={{
                            open: isMediaDrawerOpen,
                            onOpenChange: setIsMediaDrawerOpen,
                          }}
                        >
                          <MediaLibrary
                            id={tirageId}
                            type={"tirage" as TypeOptionPosition}
                            invalidateOnChange={handleInvalidateAttachments}
                            computeImageName={computeTirageImageName}
                            embedAttachmentOnSubmit={true}
                            setImageMode={(mode) => {
                              // MediaLibrary uses setImageMode(false) as a “back” action
                              // In the sheet modal context, just close the drawer.
                              if (!mode) {
                                setIsMediaDrawerOpen(false);
                              }
                            }}
                          />
                        </StyledDrawer>

                        <div className="flex items-end justify-end w-full relative">
                          {defaultTirageImage && (
                            <UniqueImageFullScreen
                              media={{
                                url: defaultTirageImage,
                                largeUrl: defaultTirageImage,
                                alt: "",
                                className:
                                  "w-full max-h-[400px] object-contain object-right",
                              }}
                            />
                          )}
                          {!defaultTirageImage && (
                            <div
                              className="w-full h-[300px] bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-slate-400 hover:bg-slate-100 transition-colors"
                              onClick={() => setIsMediaDrawerOpen(true)}
                            >
                              <ImageIcon className="w-12 h-12 text-slate-400" />
                              <p className="text-sm text-slate-500 font-medium">
                                {t("thumbnailManageImages_action")}
                              </p>
                            </div>
                          )}
                          <Button
                            variant="white"
                            className="absolute bottom-2 right-2 text-gray-950 text-xs sm:text-sm flex gap-2"
                            onClick={() => setIsMediaDrawerOpen(true)}
                          >
                            {t("formulaireBannierePhotosBouton")}
                            <Badge variant="outlined">
                              {tirageAttachments?.length ?? "0"}
                            </Badge>
                          </Button>
                        </div>
                      </div>
                    )}
                    <AF.Text name="tirageDescriptionNumeroEnregistrement" />
                    <AF.Text name="tirageDescriptionNumeroInventaire" />
                    <MultilingualField
                      render={(lang: SupportedLocale) => (
                        <ModifiableList
                          listKey="liste-types-tirages"
                          name={`tirageDescriptionType.${lang}`}
                          label={t("tirageDescriptionType")}
                          lang={lang}
                        />
                      )}
                    />
                    {/* Dimensions */}
                    <SeparatorWithText
                      text={t(
                        "tirageDescriptionDimensionsWithMarginsSeparatorTitle",
                      )}
                      className="my-4"
                    />
                    <MinifiedTwoDimensionalInput
                      parentName="tirageDescriptionDimensionsWithMargins"
                      needSurface={false}
                    />
                    <SeparatorWithText
                      text={t(
                        "tirageDescriptionDimensionsWithoutMarginsSeparatorTitle",
                      )}
                      className="my-4"
                    />
                    <MinifiedTwoDimensionalInput
                      parentName="tirageDescriptionDimensionsWithoutMargins"
                      needSurface={false}
                    />
                    <AF.Text name="tirageDescriptionDateCreation" />
                    <MultilingualField
                      render={(lang: SupportedLocale) => (
                        <AF.Col>
                          <ModifiableList
                            listKey="liste-types-printers"
                            name={`tirageDescriptionPrinter.${lang}`}
                            label={t("tirageDescriptionPrinter")}
                            lang={lang}
                          />
                          <ModifiableList
                            listKey="liste-techniques"
                            name={`tirageDescriptionTechnique.${lang}`}
                            label={t("tirageDescriptionTechnique")}
                            lang={lang}
                          />
                          <ModifiableList
                            listKey="liste-supports"
                            name={`tirageDescriptionSupport.${lang}`}
                            label={t("tirageDescriptionSupport")}
                            lang={lang}
                          />
                          <AF.Text
                            name={`tirageDescriptionEditionNumber.${lang}`}
                            label={t("tirageDescriptionEditionNumber")}
                          />
                          <AF.Text
                            name={`tirageDescriptionSignatureInscription.${lang}`}
                            label={t("tirageDescriptionSignatureInscription")}
                          />
                          <AF.Text
                            name={`tirageDescriptionSignatureInscriptionDos.${lang}`}
                            label={t(
                              "tirageDescriptionSignatureInscriptionDos",
                            )}
                            corner={() => (
                              <AF.Switch
                                name={`tirageDescriptionSignatureInscriptionDosPublished.${lang}`}
                                label={t(
                                  "tirageDescriptionSignatureInscriptionDosPublished",
                                )}
                              />
                            )}
                          />
                        </AF.Col>
                      )}
                    />
                  </AF.Col>
                  <AF.Col>
                    {/* Estimation Fields */}
                    <SeparatorWithText
                      text={t("tirageEstimationSeparatorTitle")}
                      className="my-4"
                    />
                    <AF.Text
                      name="tirageEstimationDescription"
                      label={t("tirageEstimationDescription")}
                    />
                    <AF.SelectCurrency
                      name="tirageEstimationAmount"
                      label={t("tirageEstimationAmount")}
                      className="w-full"
                    />
                    {/* Sale Fields */}
                    <SeparatorWithText
                      text={t("tirageSaleSeparatorTitle")}
                      className="my-4"
                    />
                    <AF.Text
                      name="tirageSaleDescription"
                      label={t("tirageSaleDescription")}
                    />
                    <AF.SelectCurrency
                      name="tirageSaleAmount"
                      label={t("tirageSaleAmount")}
                      className="w-full"
                    />
                    {/* Acquisition Fields */}
                    <SeparatorWithText
                      text={t("tirageAcquisitionSeparatorTitle")}
                      className="my-4"
                    />
                    <AF.Text
                      name="tirageAcquisitionDescription"
                      label={t("tirageAcquisitionDescription")}
                    />
                    <AF.SelectCurrency
                      name="tirageAcquisitionAmount"
                      label={t("tirageAcquisitionAmount")}
                      className="w-full"
                    />
                    <AF.RichText
                      name="tirageDescriptionNotes"
                      label={t("tirageDescriptionNotes")}
                    />

                    {/* Storage Fields */}
                    <SeparatorWithText
                      text={t("tirageStorageSeparatorTitle")}
                      className="my-4"
                    />
                    <ModifiableList
                      listKey="liste-storage-locations"
                      name="tirageStorageLocation1"
                      label={t("tirageStorageLocation1")}
                    />
                    <ModifiableList
                      listKey="liste-storage-locations"
                      name="tirageStorageLocation2"
                      label={t("tirageStorageLocation2")}
                    />
                    <ModifiableList
                      listKey="liste-storage-locations"
                      name="tirageStorageLocation3"
                      label={t("tirageStorageLocation3")}
                    />
                  </AF.Col>
                </AF.Row>
              </Tabs.TabsContent>
              <Tabs.TabsContent
                value="printHistory"
                className="px-11 py-5 gap-8"
              >
                <AF.Col>
                  <AbstractFormSelectFetcher
                    label={t("oeuvresLocalisationHistoriqueCollection")}
                    name="collectorId"
                    fetcher={async () => await getCollectors({})}
                    queryKey={["collectors"]}
                    allowClear={true}
                    getContent={(collector: Collector) => (
                      <>
                        {collector.collectionneursDescriptionNom}{" "}
                        {collector.collectionneursDescriptionPrenom}{" "}
                        {collector.collectionneursDescriptionVille
                          ? `(${collector.collectionneursDescriptionVille})`
                          : ""}
                      </>
                    )}
                    getLabel={(collector: Collector) =>
                      `${collector.collectionneursDescriptionNom} ${collector.collectionneursDescriptionPrenom} ${collector.collectionneursDescriptionVille ? `(${collector.collectionneursDescriptionVille})` : ""}`
                    }
                    getValue="id"
                  />
                  <AF.Checkbox name="tirageOeuvreNonLocalisee" />
                  <TirageHistoryTable tirageId={tirageId} disabled={isReader} />
                </AF.Col>
              </Tabs.TabsContent>
              <Tabs.TabsContent value="links" className="px-11 py-5 gap-8">
                <LinksManagement
                  type="tirage"
                  className="pr-0"
                  id={tirageId}
                  data={tirageData as Tirage}
                  configuration={{
                    fields: [
                      {
                        name: "links",
                        type: "links",
                        label: "Liens",
                        links: [
                          {
                            name: "tirageInAuction",
                            label: t("entryauctions"),
                            type: "link-complex",
                            fields: [
                              {
                                name: "auction.ventesDescriptionNumeroVente",
                                label: t("auctionDescriptionNumeroVente"),
                                type: "text",
                                className: "w-full",
                                disabled: true,
                              },
                              {
                                name: "auction.ventesDescriptionResume",
                                label: t("auctionDescriptionResume"),
                                type: "richtext",
                                className: "w-full",
                                disabled: true,
                              },
                              {
                                name: "tirageDansVentesDescriptionNumeroLot",
                                label: t(
                                  "tirageDansVentesDescriptionNumeroLot",
                                ),
                                type: "text",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansVentesDescriptionReproduitPage",
                                label: t(
                                  "tirageDansVentesDescriptionReproduitPage",
                                ),
                                type: "text",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansVentesDescriptionEstimationBasse",
                                label: t(
                                  "tirageDansVentesDescriptionEstimationBasse",
                                ),
                                type: "inputSelectCurrency",
                                className: "w-full",
                                defaultSelectValue: "EUR",
                              },
                              {
                                name: "tirageDansVentesDescriptionEstimationHaute",
                                label: t(
                                  "tirageDansVentesDescriptionEstimationHaute",
                                ),
                                type: "inputSelectCurrency",
                                className: "w-full",
                                defaultSelectValue: "EUR",
                              },
                              {
                                name: "tirageDansVentesDescriptionVendu",
                                label: t("tirageDansVentesDescriptionVendu"),
                                type: "checkbox",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansVentesDescriptionResultatNonListe",
                                label: t(
                                  "tirageDansVentesDescriptionResultatNonListe",
                                ),
                                type: "checkbox",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansVentesDescriptionNonVenu",
                                label: t("tirageDansVentesDescriptionNonVenu"),
                                type: "checkbox",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansVentesDescriptionNonVendu",
                                label: t("tirageDansVentesDescriptionNonVendu"),
                                type: "checkbox",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansVentesDescriptionNonReproduit",
                                label: t(
                                  "tirageDansVentesDescriptionNonReproduit",
                                ),
                                type: "checkbox",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansVentesDescriptionRetire",
                                label: t("tirageDansVentesDescriptionRetire"),
                                type: "checkbox",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansVentesDescriptionMiseAPrix",
                                label: t(
                                  "tirageDansVentesDescriptionMiseAPrix",
                                ),
                                type: "inputSelectCurrency",
                                defaultSelectValue: "EUR",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansVentesDescriptionPrixMarteau",
                                label: t(
                                  "tirageDansVentesDescriptionPrixMarteau",
                                ),
                                type: "inputSelectCurrency",
                                defaultSelectValue: "EUR",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansVentesDescriptionPrixAvecFrais",
                                label: t(
                                  "tirageDansVentesDescriptionPrixAvecFrais",
                                ),
                                type: "inputSelectCurrency",
                                defaultSelectValue: "EUR",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansVentesDescriptionNotes",
                                label: t("tirageDansVentesDescriptionNotes"),
                                type: "richtext",
                                className: "w-full",
                              },
                            ],
                          },
                          {
                            name: "tirageInExhibition",
                            label: t("entryexhibitions"),
                            type: "link-complex",
                            fields: [
                              {
                                name: "exhibition.expositionsDescriptionNumeroExpo",
                                label: t("exhibitionDescriptionNumeroExpo"),
                                type: "text",
                                className: "w-full",
                                disabled: true,
                              },
                              {
                                name: "exhibition.expositionsDescriptionResume",
                                label: t("exhibitionDescriptionResume"),
                                type: "richtext",
                                className: "w-full",
                                disabled: true,
                              },
                              {
                                name: "tirageDansExpositionsDescriptionNumeroCatalogue",
                                label: t(
                                  "tirageDansExpositionsDescriptionNumeroCatalogue",
                                ),
                                type: "text",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansExpositionsNotes",
                                label: t("tirageDansExpositionsNotes"),
                                type: "richtext",
                                className: "w-full",
                              },
                            ],
                          },
                          {
                            name: "tirageInBibliography",
                            label: t("entrybibliographies"),
                            type: "link-complex",
                            fields: [
                              {
                                name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                                label: t(
                                  "bibliographieDescriptionNumeroBibliographie",
                                ),
                                type: "text",
                                className: "w-full",
                                disabled: true,
                              },
                              {
                                name: "bibliography.bibliographieDescriptionResumeLong",
                                label: t("bibliographieDescriptionResumeLong"),
                                type: "richtext",
                                className: "w-full",
                                disabled: true,
                              },
                              {
                                name: "tirageDansBibliographieDescriptionReproduitPage",
                                label: t(
                                  "tirageDansBibliographieDescriptionReproduitPage",
                                ),
                                type: "text",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansBibliographieDescriptionCitePage",
                                label: t(
                                  "tirageDansBibliographieDescriptionCitePage",
                                ),
                                type: "text",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansBibliographieDescriptionPhotographe",
                                label: t(
                                  "tirageDansBibliographieDescriptionPhotographe",
                                ),
                                type: "text",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansBibliographieDescriptionNonPagine",
                                label: t(
                                  "tirageDansBibliographieDescriptionNonPagine",
                                ),
                                type: "checkbox",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansBibliographieDescriptionReproductionCouleur",
                                label: t(
                                  "tirageDansBibliographieDescriptionReproductionCouleur",
                                ),
                                type: "checkbox",
                                className: "w-full",
                              },
                              {
                                name: "tirageDansBibliographieNotes",
                                label: t("tirageDansBibliographieNotes"),
                                type: "richtext",
                                className: "w-full",
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  }}
                />
              </Tabs.TabsContent>
              <Tabs.TabsContent
                value="technicalNotes"
                className="px-11 py-5 gap-8"
              >
                <AF.RichText
                  name="tirageDescriptionTechnicalNotes"
                  label={t("tirageDescriptionTechnicalNotes")}
                />
              </Tabs.TabsContent>
              <Tabs.TabsContent
                value="conditionNotes"
                className="px-11 py-5 gap-8"
              >
                <AF.RichText
                  name="tirageDescriptionConditionNotes"
                  label={t("tirageDescriptionConditionNotes")}
                />
              </Tabs.TabsContent>
            </div>
          </Tabs.Root>
        </Form>
      </SheetContent>
    </Sheet>
  );
};

export default TirageSheetForm;
